import { Metric } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { fetchClientAtom } from '@/atoms/queries/client';
import { MetricDefinition, MetricLookupMap, PHYSICAL_SOURCE } from '@/types/metric';
import { objSnakeToCamel } from '@/utils/queries';

const rawMetricsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  return {
    queryKey: ['metrics'],
    queryFn: async () => (await fetch(`/metrics`)) as Promise<Metric[]>,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  };
});

export const metricDefinitionsAtom = atom(async get => ((await get(rawMetricsAtom)).data || []).map(objSnakeToCamel));

/**
 * Dual-key lookup map for O(1) metric lookups.
 * Both metric.key and metric.keyNormalised map to the full MetricDefinition.
 * Supports fast metric lookups during table enrichment.
 */
const metricLookupMapAtom = atom(async get => {
  const metricData = await get(metricDefinitionsAtom);
  const metricLookupMap: MetricLookupMap = new Map();
  metricData.forEach(metric => {
    metricLookupMap.set(metric.key, metric);
    if (metric.keyNormalised !== metric.key) {
      metricLookupMap.set(metric.keyNormalised, metric);
    }
  });
  return metricLookupMap;
});
export const unwrappedMetricLookupMapAtom = unwrap(metricLookupMapAtom, prev => prev || undefined);

const makeMetricKeyBoolMapAtom = (predicate: (metric: MetricDefinition) => boolean) =>
  atom(async get => {
    const metrics = await get(metricDefinitionsAtom);
    return metrics.reduce<Record<string, boolean>>((acc, metric) => {
      acc[metric.key] = predicate(metric);
      return acc;
    }, {});
  });

export const metricKeyIs360MapAtom = makeMetricKeyBoolMapAtom(metric => metric.is360);
export const metricKeyIsPhysicalMapAtom = makeMetricKeyBoolMapAtom(metric => metric.source === PHYSICAL_SOURCE);
